import React, { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BusinessInformationForm,
  SellerPaymentMethod,
  TaxDocumentsForm,
} from "@/components/sellerProfileForms";
import ls from "@/utils/SecureLS";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { BusinessInformationValues } from "@/schemas/sellerProfileSchema";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { updateSellerInfo } from "@/graphql/mutations";
import { setDistributorInfo } from "@/store/slices/authSlice";
import { toast } from "sonner";
import {
  fetchSellerInfoQueryOptions,
  fetchSellerPaymentMethodsQueryOptions,
} from "@/queryOptions/seller/fetchSellerInfo";
import { useQuery } from "@tanstack/react-query";
import { getUserFromLS } from "@/utils/helper/getUserFromLS";
import { LoaderCircle } from "lucide-react";

const SellerProfile = () => {
  const user = getUserFromLS();
  const dispatch = useAppDispatch();
  const [sellerInfoLoading, setSellerInfoLoading] = useState(false);
  const { data: distributorInfo, isLoading: distributorInfoLoading } = useQuery(
    fetchSellerInfoQueryOptions({
      user_id: { eq: user?.user_id },
    },{
      enabled: !!user?.user_id,
    })
  );
  const { data: sellerPaymentMethods, isLoading } = useQuery(
    fetchSellerPaymentMethodsQueryOptions(
      { seller_id: { eq: user?.user_id } },
      {
        enabled: !!user?.user_id,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
      }
    )
  );

  const businessFormSubmit = useCallback(
    async (data: BusinessInformationValues) => {
      setSellerInfoLoading(true);
      const sellerBusinessInfo = await executeGraphQLMutation({
        query: updateSellerInfo,
        variables: {
          input: {
            id: distributorInfo?.[0]?.id,
            user_id: user?.user_id,
            business_name: data?.businessName,
            phone: data?.phone,
            street: data?.street,
            suite: data?.suite || "",
            city: data?.city,
            state: data?.state,
            country: data?.country,
            tax_id: data?.taxId,
            postal_code: data?.zipCode,
            updated_at: new Date().toISOString(),
          },
        },
      });

      if (sellerBusinessInfo.id) {

        toast.success("Business information updated successfully");
      }
      setSellerInfoLoading(false);
    },
    [dispatch, distributorInfo, user]
  );

  return (
    <div className="container py-16 pb-24 px-8 md:px-6 md:max-w-[90rem] h-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Seller Profile</h1>
        <p className="text-gray-600 mt-2">
          Manage your business information, tax documents, and payment methods.
        </p>
      </div>

      <Tabs defaultValue="business" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="business">Business Information</TabsTrigger>
          <TabsTrigger value="tax">Tax Documents</TabsTrigger>
          <TabsTrigger value="payment">Payment Methods</TabsTrigger>
        </TabsList>

        <TabsContent value="business" className="mt-6">
          {distributorInfo && user?.user_id === distributorInfo?.[0]?.user_id && !distributorInfoLoading && (
            <BusinessInformationForm
              onSubmit={businessFormSubmit}
              loading={sellerInfoLoading}
              defaultValues={{
                ...distributorInfo?.[0],
                contact_name: user?.first_name + " " + user?.last_name,
                email: user?.email,
              }}
            />
          )}
          {distributorInfoLoading && (
            <div className="flex items-center justify-center flex-1 min-h-[400px]">
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center">
                  <LoaderCircle className="w-6 h-6 animate-spin mr-4" />
                  <span>Fetching your business information. This won't take long!</span>
                </div>
                <div className="text-sm text-gray-500 text-center max-w-md">
                  If this takes too long, try refreshing the page
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="tax" className="mt-6">
          {distributorInfo && user?.user_id === distributorInfo?.[0]?.user_id && (
            <TaxDocumentsForm
              taxDocuments={distributorInfo?.[0]?.tax_documents || []}
              distributorInfo={distributorInfo?.[0]}
            />
          )}
        </TabsContent>

        <TabsContent value="payment" className="mt-6">
          <SellerPaymentMethod
            paymentMethods={sellerPaymentMethods?.[0]}
            sellerId={user?.user_id}
            loading={isLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SellerProfile;
